import React from "react";
import { Tooltip } from "components/common/Tooltip";

export interface FigureRow {
  key: string;
  /** One per heading: the first is the label, the rest are figures. */
  cells: React.ReactNode[];
  tone?: "muted" | "total" | "selected";
  /** Shown on hovering the row. */
  hint?: React.ReactNode;
  /** A trailing control, in a column only as wide as it. */
  action?: React.ReactNode;
  testId?: string;
}

const TONE = {
  muted: "text-fg-muted",
  total: "font-medium",
  selected: "bg-selected-light",
} as const;

/** A label column, right-aligned tabular figures, and hairlines between rows:
 *  the shape of every usage breakdown in settings. */
export const FigureTable: React.FC<{
  head: React.ReactNode[];
  rows: FigureRow[];
  testId?: string;
}> = ({ head, rows, testId }) => {
  const actions = rows.some((r) => r.action);
  const align = (i: number) => (i === 0 ? "text-left" : "text-right");
  return (
    <table className="w-full tabular-nums" data-testid={testId}>
      <thead>
        <tr className="text-fg-muted-regular-small">
          {head.map((h, i) => (
            <th key={i} className={`font-normal py-1 ${align(i)}`}>
              {h}
            </th>
          ))}
          {actions && <th />}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const tr = (
            <tr
              key={r.key}
              data-testid={r.testId}
              className={`border-t border-border ${r.tone ? TONE[r.tone] : ""} ${r.hint ? "hover:bg-bg-hover" : ""}`}
            >
              {r.cells.map((c, i) => (
                <td
                  key={i}
                  className={`py-1 ${i === 0 ? "truncate" : ""} ${align(i)}`}
                >
                  {c}
                </td>
              ))}
              {actions && <td className="py-1 text-right w-0">{r.action}</td>}
            </tr>
          );
          return r.hint ? (
            <Tooltip key={r.key} title={r.hint} followCursor>
              {tr}
            </Tooltip>
          ) : (
            tr
          );
        })}
      </tbody>
    </table>
  );
};
